
import { useNavigate } from 'react-router';
import React, { ReactNode, createContext, useContext, useEffect } from 'react';

import { FlagSyncLoader } from '@/components/svgs/logo';

import { useGetAppContext } from '@/services/context/queries';
import { AppContext } from '@/services/types';

type AppContextContextType = {
  appContext: AppContext;
};

const AppContextContext = createContext<AppContextContextType | null>(null);

type AppContextProviderProps = {
  children: ReactNode;
};

export const AppContextProvider: React.FC<AppContextProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { data: appContext, isLoading, isError } = useGetAppContext();

  useEffect(() => {
    if (isError) {
      navigate('/login', { replace: true });
    }
  }, [isError, navigate]);

  // Until /user/me resolves we don't know if the visitor is authenticated, so
  // show a NEUTRAL loader — never the dashboard chrome. Rendering the dashboard
  // skeleton here flashed the full sidebar to logged-out users before booting
  // them to /login. (Matches the app-shell Suspense fallback for a seamless
  // hand-off.)
  if (isLoading || isError || !appContext) {
    return (
      <div className="flex h-screen items-center justify-center">
        <FlagSyncLoader className="h-12 w-12" />
      </div>
    );
  }

  return (
    <AppContextContext.Provider value={{ appContext }}>
      {children}
    </AppContextContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContextContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};
